import React from 'react';
import type { ElementRef } from 'react';

import type { StyleProps } from '@backyard-ui/styles';

import { Item, Indicator } from '@radix-ui/react-radio-group';
import type { RadioGroupItemProps as ItemPropsBase } from '@radix-ui/react-radio-group';

import { useRadioGroupContext } from './RadioGroup.context';
import { useFormControl } from '../FormControl/use-form-control';
import type { FormControlOptions } from '../FormControl';
import * as Card from '../Card';
import { Stack } from '../Stack';

import styles from './CardItem.styles';

export interface RadioGroupCardItemProps
  extends StyleProps<Omit<ItemPropsBase, 'asChild'>>,
    FormControlOptions {}

const RadioGroupCardItem = React.forwardRef<
  ElementRef<typeof Item>,
  RadioGroupCardItemProps
>((props, ref) => {
  const {
    value,
    UNSAFE_className,
    UNSAFE_style,
    isDisabled,
    isReadOnly,
    children,
    ...rest
  } = props;

  const input = useFormControl({
    isDisabled: isDisabled || isReadOnly,
    isReadOnly,
    ...rest,
  });
  const { size } = useRadioGroupContext();

  return (
    <Card.Root radius="md" size="sm" asChild hasBorder>
      <Item
        className={styles({ size }).input({
          className: UNSAFE_className,
        })}
        value={value}
        ref={ref}
        {...input}
      >
        <Card.Body>
          <Stack direction="row" align="center">
            <span className={styles({ size }).indicatorWrapper()}>
              <Indicator className={styles().indicator()} />
            </span>
            {children}
          </Stack>
        </Card.Body>
      </Item>
    </Card.Root>
  );
});

RadioGroupCardItem.displayName = '@backyard-ui/core/RadioGroupCardItem';

export default RadioGroupCardItem;
